/**
 * Suspense fallback rendered while a lazy-loaded route chunk downloads.
 *
 * Two variants:
 *  - <RouteLoadingFallback /> — full-page: logo + spinner + text.
 *    Matches the branded "app is loading" moment the stakeholder wants
 *    on every route transition. NOT a data-shape skeleton — that reads
 *    as fake content the first time a user sees it.
 *  - <InlineLoadingFallback /> — compact spinner-only, for small
 *    boundaries where a full-page splash would be jarring.
 */

export function RouteLoadingFallback() {
  return (
    <div
      className="flex items-center justify-center min-h-screen bg-[#F4F5FA] dark:bg-[#28243D]"
      aria-busy="true"
      aria-label="Loading"
    >
      <div className="flex flex-col items-center gap-4 text-center">
        <img src="/edemics-logo.png" alt="Edemics" className="h-10 w-auto" />
        <div
          className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"
          role="status"
          aria-label="Loading spinner"
        />
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  )
}

export function InlineLoadingFallback({ label = "Loading" }: { label?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="inline-flex items-center gap-2 text-sm text-muted-foreground"
    >
      <span
        className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-primary border-r-transparent"
        aria-hidden
      />
      {label}
    </div>
  )
}
